import authService from "../services/authService";
import { decodeJwtPayload } from "./jwt";

export const ROLES = {
  ADMIN: "admin",
  VIEWER: "viewer",
  USER: "user",
  REPORTER: "reporter",
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

export const normalizeRoleKey = (role: unknown): string => {
  const value = String(role ?? "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();

  return value;
};

export const normalizeRoles = (roles: unknown): string[] => {
  if (Array.isArray(roles)) {
    return roles
      .filter(Boolean)
      .map((role) => normalizeRoleKey(role))
      .filter(Boolean);
  }

  if (typeof roles === "string" && roles.trim()) {
    return roles
      .split(/[,;]+/)
      .map((role) => normalizeRoleKey(role))
      .filter(Boolean);
  }

  return [];
};

export const normalizeGroupPath = (group: unknown): string => {
  const value = String(group ?? "").trim().toLowerCase();
  if (!value) {
    return "";
  }
  return value.startsWith("/") ? value : `/${value}`;
};

export const rolesInclude = (roles: string[], expected: string): boolean => {
  const expectedKey = normalizeRoleKey(expected);
  return roles.some((role) => normalizeRoleKey(role) === expectedKey);
};

export const extractRolesFromTokenPayload = (payload: Record<string, unknown> | null): string[] => {
  if (!payload) {
    return [];
  }

  const topLevelRoles = normalizeRoles(payload.roles);
  const realmRoles = normalizeRoles((payload.realm_access as { roles?: unknown } | undefined)?.roles);
  const resourceRoles = normalizeRoles(
    Object.values((payload.resource_access as Record<string, { roles?: unknown }> | undefined) || {})
      .flatMap((resource) => resource?.roles || []),
  );

  return Array.from(new Set([...topLevelRoles, ...realmRoles, ...resourceRoles]));
};

export const extractGroupsFromTokenPayload = (payload: Record<string, unknown> | null): string[] => {
  if (!payload || !Array.isArray(payload.groups)) {
    return [];
  }

  return Array.from(
    new Set(payload.groups.map((group) => normalizeGroupPath(group)).filter(Boolean)),
  );
};

export const extractTenantIdFromTokenPayload = (
  payload: Record<string, unknown> | null,
): string | undefined => {
  if (!payload) {
    return undefined;
  }

  const tenantId = payload.tenant_id ?? payload.tenantId;
  if (typeof tenantId === "string" && tenantId.trim()) {
    return tenantId.trim().toLowerCase();
  }

  return undefined;
};

export const extractRolesFromUser = (user: any): string[] => {
  if (!user) {
    return [];
  }

  const profile = user?.profile || {};
  const accessPayload = decodeJwtPayload(user?.access_token);
  const idPayload = decodeJwtPayload(user?.id_token);

  return Array.from(
    new Set([
      ...extractRolesFromTokenPayload(profile),
      ...extractRolesFromTokenPayload(accessPayload),
      ...extractRolesFromTokenPayload(idPayload),
    ]),
  );
};

export const extractTenantContextFromUser = (
  user: any,
): { tenantId?: string; groups: string[] } => {
  if (!user) {
    return { groups: [] };
  }

  const profile = user?.profile || {};
  const accessPayload = decodeJwtPayload(user?.access_token);
  const idPayload = decodeJwtPayload(user?.id_token);
  const payloads = [profile, accessPayload, idPayload].filter(
    (payload): payload is Record<string, unknown> => !!payload,
  );

  let tenantId: string | undefined;
  const groups = new Set<string>();

  for (const payload of payloads) {
    if (!tenantId) {
      tenantId = extractTenantIdFromTokenPayload(payload);
    }
    extractGroupsFromTokenPayload(payload).forEach((group) => groups.add(group));
  }

  return { tenantId, groups: Array.from(groups) };
};

export const isTenantAdminFromGroups = (
  tenantId: string | undefined,
  groups: string[],
): boolean => {
  if (!tenantId) {
    return false;
  }

  const expectedGroup = `/${tenantId}/admin`;
  return groups.some((group) => normalizeGroupPath(group) === expectedGroup);
};

export const getCurrentUserRoles = async (): Promise<string[]> => {
  try {
    const user = await authService.getUser();
    return extractRolesFromUser(user);
  } catch (error) {
    console.error("Failed to load user roles:", error);
    return [];
  }
};

export const hasRole = (roles: string[], role: string) => rolesInclude(roles, role);

export const isAdmin = (roles: string[]) => hasRole(roles, ROLES.ADMIN);

export const canManageAnomalyGuides = (roles: string[]) => isAdmin(roles);

export const isViewer = (roles: string[]) => hasRole(roles, ROLES.VIEWER);

/**
 * Tenant admin when JWT groups contains `/{tenant_id}/admin`
 * and the user has the `user` role (not platform admin).
 */
export const isTenantAdminUser = (roles: string[], user?: any): boolean => {
  if (isAdmin(roles)) {
    return false;
  }
  if (!hasRole(roles, ROLES.USER)) {
    return false;
  }

  const { tenantId, groups } = extractTenantContextFromUser(user);
  return isTenantAdminFromGroups(tenantId, groups);
};

export const canAccessProfilesMenu = (roles: string[], user?: any) =>
  isTenantAdminUser(roles, user);

export type RouteAccessConfig = {
  allowedRoles?: string[];
  tenantUserProfileOnly?: boolean;
};

export type RouteAccessOptions = {
  profilesMenuVisible?: boolean;
  user?: any;
};

export const canAccessRouteEntry = (
  roles: string[],
  route: RouteAccessConfig,
  options?: RouteAccessOptions,
): boolean => {
  if (route.tenantUserProfileOnly) {
    if (options?.profilesMenuVisible === true) {
      return true;
    }
    return canAccessProfilesMenu(roles, options?.user);
  }
  return canAccessRoute(roles, route.allowedRoles);
};

export const canAccessRoute = (roles: string[], allowedRoles?: string[]) => {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }
  if (isAdmin(roles)) {
    return true;
  }
  return roles.some((role) => allowedRoles.includes(role));
};
